'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@common/components';
import SvgMagnifyingGlass from '@common/icon/SvgMagnifyingGlass';
import SvgNotification from '@common/icon/SvgNotification';
import { cn } from '@core/utils';
import { usePathname } from 'next/navigation';
import { Suspense } from 'react';
import { CustomLink } from '@/app/_components/CustomLink';
import { HeaderLogo } from '@/app/_components/HeaderLogo';
import { HeaderSearchContainer, useSearchOverlayStore } from '@/app/_components/SearchBar';
import { shouldShowDesktopTrendKeyword } from '@/app/_components/TrendKeyword/constants';
import { TrendKeywordInline } from '@/app/_components/TrendKeyword/TrendKeywordInline';
import { HeaderProfileAvatar } from '@/app/(allowed)/my/_components/HeaderProfileAvatar';
import { HIDE_DESKTOP_HEADER_PATHNAMES } from '@/components/template/GlobalLayout/constants';
import { DONATION_ROUTES, ROUTES, SELECTOR, WEL_MARKET_ROUTES } from '@/constants';
import { SUB_DOMAIN } from '@/constants/domain';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { usePartnerChromeClassName } from '@/hooks/usePartnerChromeClassName';
import { useAuthStore } from '@/stores/AuthStore';
import { useDeviceInfoStore } from '@/stores/DeviceStore';
import { scrollToPageTop } from '@/utils';
interface DesktopNavItem {
  label: string;
  href: string;
  isActive: (pathname: string) => boolean;
}
const isSectionPath = (pathname: string, sectionRoot: string) => pathname === sectionRoot || pathname.startsWith(`${sectionRoot}/`);
const DESKTOP_NAV_ITEMS: DesktopNavItem[] = [{
  label: '홈',
  href: ROUTES.HOME.pathname,
  isActive: pathname => pathname === ROUTES.HOME.pathname
}, {
  label: '고향사랑기부',
  href: DONATION_ROUTES.HOME.pathname,
  isActive: pathname => isSectionPath(pathname, DONATION_ROUTES.HOME.pathname)
}];
export const DesktopHeader = () => {
  const $ = _c(43);
  const partnerChromeClassName = usePartnerChromeClassName();
  const isLogin = useAuthStore(_temp);
  const pathname = usePathname() ?? "";
  const subDomain = useDeviceInfoStore(_temp2);
  const router = useCustomRouter();
  const isOverlayOpen = useSearchOverlayStore(_temp3);
  const openOverlay = useSearchOverlayStore(_temp4);
  const closeOverlay = useSearchOverlayStore(_temp5);
  const isHiddenPage = HIDE_DESKTOP_HEADER_PATHNAMES.includes(pathname);
  const isLocalMarketPath = isSectionPath(pathname, WEL_MARKET_ROUTES.HOME.pathname);
  const shouldHideTossLocalMarketHeader = isLocalMarketPath && subDomain === SUB_DOMAIN.TOSS;
  if (isHiddenPage || shouldHideTossLocalMarketHeader) {
    return null;
  }
  const isSearchPage = pathname === ROUTES.SEARCH.pathname;
  let t0;
  if ($[0] !== closeOverlay || $[1] !== isOverlayOpen || $[2] !== isSearchPage || $[3] !== openOverlay || $[4] !== router) {
    t0 = () => {
      if (!isSearchPage) {
        router.push(ROUTES.SEARCH.pathname);
        return;
      }
      (isOverlayOpen ? closeOverlay : openOverlay)();
    };
    $[0] = closeOverlay;
    $[1] = isOverlayOpen;
    $[2] = isSearchPage;
    $[3] = openOverlay;
    $[4] = router;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  const handleSearchClick = t0;
  const isHomePage = pathname === ROUTES.HOME.pathname;
  let t1;
  if ($[6] !== isHomePage) {
    t1 = e => {
      if (!isHomePage) {
        return;
      }
      e.preventDefault();
      scrollToPageTop();
    };
    $[6] = isHomePage;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const handleHomeClick = t1;
  let t2;
  if ($[8] !== partnerChromeClassName) {
    t2 = cn(partnerChromeClassName, "sticky top-0 z-20 w-full border-b border-Gray-200 bg-Gray-white mobile:hidden tablet:hidden");
    $[8] = partnerChromeClassName;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <HeaderLogo />;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== handleHomeClick) {
    t4 = <CustomLink href={ROUTES.HOME.pathname} className="flex shrink-0 items-center" aria-label={"Wello \uD648"} onClick={handleHomeClick}>{t3}</CustomLink>;
    $[11] = handleHomeClick;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== handleHomeClick || $[14] !== pathname) {
    t5 = DESKTOP_NAV_ITEMS.map(tab => <NavLink key={tab.href} href={tab.href} isActive={tab.isActive(pathname)} onClick={tab.href === ROUTES.HOME.pathname ? handleHomeClick : undefined}>{tab.label}</NavLink>);
    $[13] = handleHomeClick;
    $[14] = pathname;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== t5) {
    t6 = <div className="flex items-center gap-4">{t5}</div>;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t4 || $[19] !== t6) {
    t7 = <div className="flex items-center gap-32">{t4}{t6}</div>;
    $[18] = t4;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== pathname) {
    t8 = shouldShowDesktopTrendKeyword(pathname) && <TrendKeywordInline />;
    $[21] = pathname;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <SvgMagnifyingGlass className="size-24" />;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== handleSearchClick) {
    t10 = <button type="button" aria-label={"\uAC80\uC0C9"} onClick={handleSearchClick} className="flex size-40 shrink-0 items-center justify-center p-0 text-Gray-700 transition-colors hover:text-Gray-900">{t9}</button>;
    $[24] = handleSearchClick;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <CustomLink href={ROUTES.PUSH.pathname} aria-label={"\uC54C\uB9BC\uC73C\uB85C \uC774\uB3D9"} className="flex size-40 shrink-0 items-center justify-center p-0 text-Gray-700 transition-colors hover:text-Gray-900"><SvgNotification className="size-24" /></CustomLink>;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  let t13;
  if ($[27] !== isLogin) {
    t12 = <HeaderProfileAvatar showProfileAvatar={isLogin} />;
    t13 = isLogin === false && <Button asChild={true} data-no-donation-accent={true} dimensions="H40" roundness="capsule"><CustomLink href={ROUTES.LOGIN.pathname}>로그인</CustomLink></Button>;
    $[27] = isLogin;
    $[28] = t12;
    $[29] = t13;
  } else {
    t12 = $[28];
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t10 || $[31] !== t12 || $[32] !== t13 || $[33] !== t8) {
    t14 = <div className="flex items-center gap-12">{t8}{t10}{t11}{t12}{t13}</div>;
    $[30] = t10;
    $[31] = t12;
    $[32] = t13;
    $[33] = t8;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== t14 || $[36] !== t7) {
    t15 = <nav className="mx-auto flex h-72 max-w-screen-xl items-center justify-between px-40">{t7}{t14}</nav>;
    $[35] = t14;
    $[36] = t7;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  let t17;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Suspense fallback={null}><HeaderSearchContainer /></Suspense>;
    t17 = <div id={SELECTOR.DESKTOP_HEADER_ATTACHMENT} className="absolute inset-x-0 top-full" />;
    $[38] = t16;
    $[39] = t17;
  } else {
    t16 = $[38];
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t15 || $[41] !== t2) {
    t18 = <header className={t2}>{t15}{t16}{t17}</header>;
    $[40] = t15;
    $[41] = t2;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  return t18;
};
const NavLink = t0 => {
  const $ = _c(8);
  const {
    href,
    isActive,
    children,
    onClick
  } = t0;
  const t1 = isActive ? "page" : undefined;
  const t2 = isActive ? "text-Gray-900" : "text-Gray-600 hover:text-Gray-900";
  let t3;
  if ($[0] !== t2) {
    t3 = cn("flex h-40 items-center px-12 font-600 transition-colors typo-body2", t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== children || $[3] !== href || $[4] !== onClick || $[5] !== t1 || $[6] !== t3) {
    t4 = <CustomLink href={href} aria-current={t1} className={t3} onClick={onClick}>{children}</CustomLink>;
    $[2] = children;
    $[3] = href;
    $[4] = onClick;
    $[5] = t1;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
};
function _temp(state) {
  return state.isLogin;
}
function _temp2(state_0) {
  return state_0.subDomain;
}
function _temp3(state_1) {
  return state_1.isOverlayOpen;
}
function _temp4(state_2) {
  return state_2.openOverlay;
}
function _temp5(state_3) {
  return state_3.closeOverlay;
}